import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import ConsentStatusWidget from '../components/ConsentStatusWidget';
import ChangePasswordSection from '../components/ChangePasswordSection';
import DeleteAccountSection from '../components/DeleteAccountSection';
import SiteFooter from '../components/SiteFooter';
import { postOnboarding } from '../onboarding/onboardingApi';
import DemoSchoolSettings from '../demo/DemoSchoolSettings';
import { useTranslation } from 'react-i18next';
import { getEffectiveNavRole, isPlatformAdmin } from '../utils/roles';
import { getApiBaseUrl } from '../apiBase';

export default function ProfilePage() {
    const { t } = useTranslation(['common', 'nav', 'messages', 'errors', 'profile', 'auth', 'marketplace']);
    const username = localStorage.getItem('username') || t('profile:defaultUsername');
    const role = getEffectiveNavRole();
    const admin = isPlatformAdmin();
    const [isBetaPartner, setIsBetaPartner] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        axios.get(`${getApiBaseUrl()}/beta-offer/status`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => {
                if (res.data?.isBetaPartner || res.data?.alreadyAccepted) {
                    setIsBetaPartner(true);
                }
            })
            .catch(() => {});
    }, []);
    const hint = useMemo(() => {
        const key = ['Owner', 'Coach', 'Member', 'Admin'].includes(role) ? role : 'Member';
        const h = t(`profile:roleHints.${key}`, { returnObjects: true });
        const links = [];
        if (key === 'Owner') {
            links.push({ href: '/school', label: h.schoolLink });
            links.push({ href: '/billing', label: h.billingLink });
        } else if (key === 'Coach') {
            links.push({ href: '/schedule', label: h.scheduleLink });
        } else if (key === 'Member') {
            links.push({ href: '/my-balance', label: h.balanceLink });
            links.push({ href: '/rental', label: h.rentalLink });
        } else if (key === 'Admin') {
            links.push({ href: '/school', label: h.schoolsLink });
        }
        return { title: h.title, text: h.text, links };
    }, [role, t]);
    const [resetting, setResetting] = useState(false);

    async function handleResetOnboarding() {
        if (!window.confirm(t('profile:resetOnboardingConfirm'))) return;
        setResetting(true);
        try {
            await postOnboarding('reset');
            const dest = role === 'Member' ? '/' : role === 'Coach' ? '/schedule' : '/school';
            window.location.href = dest;
        } catch {
            alert(t('profile:resetOnboardingFailed'));
        }
        setResetting(false);
    }

    return (
        <OwnerPageLayout
            title={t('nav:profile')}
            subtitle={`${username} · ${role}`}
            maxWidth={720}
        >
            {isBetaPartner && (
                <div className="founder-badge" style={{
                    background: 'linear-gradient(135deg, #FFF8E1, #FFECB3)',
                    border: '1px solid #FFE082',
                    borderRadius: 12,
                    padding: '12px 16px',
                    marginBottom: 16,
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#795548',
                    textAlign: 'center',
                }}>
                    🏅 {t('profile:founderBadge')}
                </div>
            )}
            <div className="sp-panel" style={{ marginBottom: 20, fontSize: 14, color: 'var(--sp-gray-600)' }}>
                <strong>{hint.title}</strong>
                <p style={{ margin: '8px 0 12px', lineHeight: 1.5 }}>{hint.text}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {hint.links.map(l => (
                        <Link key={l.href} to={l.href} style={{
                            color: 'var(--sp-primary)', fontWeight: 600, textDecoration: 'none', fontSize: 14
                        }}>
                            {l.label}
                        </Link>
                    ))}
                </div>
            </div>

            {(role === 'Owner' || role === 'owner') ? <DemoSchoolSettings /> : null}

            {(role === 'Owner' || role === 'Coach' || role === 'Member') ? (
                <button
                    type="button"
                    className="sp-btn sp-btn--outline"
                    onClick={handleResetOnboarding}
                    disabled={resetting}
                    style={{ marginBottom: 20, width: '100%' }}
                >
                    {resetting ? t('profile:resetting') : t('profile:resetOnboarding')}
                </button>
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                <Link to="/profile/sport" className="sp-btn sp-btn--primary" style={{ textAlign: 'center', textDecoration: 'none' }}>
                    🏆 {t('marketplace:profileEdit.title')}
                </Link>
                <Link to="/profile/marketplace-settings" className="sp-btn sp-btn--outline" style={{ textAlign: 'center', textDecoration: 'none' }}>
                    {t('marketplace:visibility.title')}
                </Link>
                <Link to="/marketplace" className="sp-btn sp-btn--outline" style={{ textAlign: 'center', textDecoration: 'none' }}>
                    {t('marketplace:title')}
                </Link>
            </div>

            {!admin ? (
                <Link
                    to="/account-type"
                    className="sp-btn sp-btn--outline"
                    style={{ marginBottom: 20, width: '100%', display: 'block', textAlign: 'center', textDecoration: 'none' }}
                >
                    ➕ {t('auth:addProfile')}
                </Link>
            ) : null}

            <ConsentStatusWidget />

            <ChangePasswordSection />
            <DeleteAccountSection />

            <SiteFooter />
        </OwnerPageLayout>
    );
}
